import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [tx, setTx] = useState(null);
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
        if (++attempts >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (++attempts >= 8) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="nx-container py-24 flex justify-center">
      <div className="max-w-md w-full text-center border border-slate-900 p-10 bg-white">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="animate-spin mx-auto text-blue-700 mb-4" />
            <h1 className="text-3xl font-black tracking-tighter mb-2">Confirming payment...</h1>
            <p className="text-slate-600">This may take a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-600 mb-4" />
            <span className="mono text-[11px] text-emerald-700">ORDER/CONFIRMED</span>
            <h1 className="text-4xl font-black tracking-tighter mt-2 mb-4">Payment successful.</h1>
            <p className="text-slate-600 mb-2">Total charged: <span className="font-black">${tx?.amount?.toFixed(2)} {tx?.currency?.toUpperCase()}</span></p>
            <p className="text-slate-600 mb-8">A confirmation has been logged.</p>
            <div className="flex gap-3 justify-center">
              <Link to="/dashboard" data-testid="view-orders-btn" className="nx-btn-primary">View Orders</Link>
              <Link to="/products" data-testid="continue-btn" className="border border-slate-900 px-6 py-3 font-bold">Continue</Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle size={56} className="mx-auto text-red-600 mb-4" />
            <h1 className="text-3xl font-black tracking-tighter mb-2">
              {status === "timeout" ? "Still processing" : "Payment failed"}
            </h1>
            <p className="text-slate-600 mb-8">
              {status === "timeout" ? "It's taking longer than usual. Check your dashboard shortly." : "Something went wrong. Please try again."}
            </p>
            <Link to="/cart" data-testid="back-cart-btn" className="nx-btn-primary">Back to Cart</Link>
          </>
        )}
      </div>
    </div>
  );
}
